import { useDetailsDrawer } from '@src/components/DetailsDrawer/useDetailsDrawer';
import { MultilegTooltip } from '@src/components/Multileg/MultilegTooltip';
import { useIsMobileLayout } from '@src/hooks/useIsMobileLayout';
import {
  type AgGridMeterProps,
  Box,
  Button,
  Card,
  Flex,
  FormControlSizes,
  IconName,
  isMultileg,
  isOrderRejected,
  Meter,
  type MeterWithValueProps,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  Order,
  type OrderStrategy,
  Popover,
  PopperHeader,
  ToggleGroup,
  UnifiedLiquidityEnum,
  useMixpanel,
  useMobileModal,
  usePopoverState,
  useSecurity,
  useStrategiesContext,
} from '@talos/kyoko';
import { type MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTradingOrderDetailsRoute } from '../../containers/Routes/routes';
import { useCurrentAppLayoutId } from '../../hooks/useAppLayoutId';
import { LAYOUT_HEADER_HEIGHT } from '../../styles/dimensions';
import { OrderCardHeader } from '../OMS/OMSCard/OrderCardHeader';
import { FilledMarketDistributionChart } from './FilledMarketDistributionChart';
import { MarketDistributionLegEnum } from './types';

function useMarketDistributionOrderMeta(order: Order) {
  const { strategiesByName } = useStrategiesContext();
  const selectedStrategy = order.Strategy ? strategiesByName?.get(order.Strategy) : undefined;
  return { selectedStrategy };
}

function usePopoverHeightObserver(onHeightChange?: () => void) {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const previousHeightRef = useRef(0);
  const updateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ResizeObserver to monitor height changes
  useEffect(() => {
    if (!tooltipRef.current || !onHeightChange) {
      return;
    }

    const resizeObserver = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry) {
        return;
      }

      const currentHeight = entry.contentRect.height;
      const previousHeight = previousHeightRef.current;

      // Only trigger update if height actually changed
      if (Math.abs(currentHeight - previousHeight) > 1) {
        previousHeightRef.current = currentHeight;

        // Debounce the update to prevent rapid successive calls
        if (updateTimeoutRef.current) {
          clearTimeout(updateTimeoutRef.current);
        }

        updateTimeoutRef.current = setTimeout(() => {
          onHeightChange();
        }, 16); // ~60fps debounce
      }
    });

    resizeObserver.observe(tooltipRef.current);

    return () => {
      resizeObserver.disconnect();
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [onHeightChange]);

  return tooltipRef;
}

function MarketDistributionBody({
  order,
  meterProps,
  selectedStrategy,
}: {
  order: Order;
  meterProps: MeterWithValueProps;
  selectedStrategy?: OrderStrategy;
}) {
  return (
    <>
      <OrderCardHeader order={order} selectedStrategy={selectedStrategy} />
      <MultilegTooltip meterProps={meterProps} data={order} />
      <FilledMarketDistribution order={order} />
    </>
  );
}

function DesktopMarketDistributionTooltip({
  order,
  meterProps,
  close,
  onHeightChange,
}: {
  order: Order;
  meterProps: MeterWithValueProps;
  close: () => void;
  onHeightChange?: () => void;
}) {
  const mixpanel = useMixpanel();
  const navigate = useNavigate();
  const layoutId = useCurrentAppLayoutId();
  const { selectedStrategy } = useMarketDistributionOrderMeta(order);
  const tooltipRef = usePopoverHeightObserver(onHeightChange);

  const handleOpenAnalytics = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      close();
      mixpanel.track(MixpanelEvent.ViewFullAnalytics, {
        [MixpanelEventProperty.Source]: MixpanelEventSource.FilledPercentModal,
      });
      navigate(
        getTradingOrderDetailsRoute({
          orderID: order.OrderID,
          type: 'order',
          tab: 'analytics',
          layoutId,
        })
      );
    },
    [close, layoutId, mixpanel, navigate, order.OrderID]
  );

  return (
    <Flex
      ref={tooltipRef}
      flexDirection="column"
      gap="spacing8"
      w="520px"
      minHeight="300px"
      maxHeight={`calc(100vh - ${LAYOUT_HEADER_HEIGHT * 2}px)`}
      overflowY="auto"
      position="relative"
      data-testid="market-distribution"
    >
      <PopperHeader
        icon={IconName.ChartDonut}
        title="Market Distribution"
        suffix={
          <Button startIcon={IconName.Exit} size={FormControlSizes.Small} onClick={handleOpenAnalytics}>
            View Full Analytics
          </Button>
        }
        onCloseClicked={close}
      />
      <Flex flexDirection="column" p="spacing8" gap="spacing4" flex="1 1 auto">
        <MarketDistributionBody order={order} meterProps={meterProps} selectedStrategy={selectedStrategy} />
      </Flex>
    </Flex>
  );
}

function MobileMarketDistributionSheet({ order, meterProps }: { order: Order; meterProps: MeterWithValueProps }) {
  const mixpanel = useMixpanel();
  const { openDetailsDrawer } = useDetailsDrawer();
  const { selectedStrategy } = useMarketDistributionOrderMeta(order);

  const handleOpenAnalytics = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      mixpanel.track(MixpanelEvent.ViewFullAnalytics, {
        [MixpanelEventProperty.Source]: MixpanelEventSource.FilledPercentModal,
      });
      openDetailsDrawer({ entity: order });
    },
    [mixpanel, openDetailsDrawer, order]
  );

  return (
    <Flex flexDirection="column" gap="spacing8" w="100%" data-testid="market-distribution">
      <Flex flexDirection="column" gap="spacing4" flex="1 1 auto">
        <MarketDistributionBody order={order} meterProps={meterProps} selectedStrategy={selectedStrategy} />
        <Box pt="spacing8" pb="spacing16">
          <Button startIcon={IconName.Exit} size={FormControlSizes.Small} onClick={handleOpenAnalytics}>
            View Full Analytics
          </Button>
        </Box>
      </Flex>
    </Flex>
  );
}

function DesktopMarketDistributionPopover({ order, meterProps }: { order: Order; meterProps: MeterWithValueProps }) {
  const mixpanel = useMixpanel();
  const popover = usePopoverState({
    trigger: 'hover',
    usePortal: true,
    noPaddingAndBorder: true,
    placement: 'right',
    delay: 500,
    closeOnClickOutside: true,
    modifiers: [
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['left'],
        },
      },
      {
        name: 'maxHeight',
        enabled: false,
      },
      {
        name: 'preventOverflow',
        enabled: true,
      },
    ],
    onOpen: () => {
      mixpanel.track(MixpanelEvent.ViewMarketDistributionTooltip, {
        [MixpanelEventProperty.Source]: MixpanelEventSource.OrdersBlotter,
      });
    },
  });

  const handleHeightChange = useCallback(() => {
    if (popover.update) {
      popover.update();
    }
  }, [popover]);

  return (
    <Popover {...popover} targetStyle={{ width: '100%' }}>
      <Meter {...meterProps} />
      <DesktopMarketDistributionTooltip
        order={order}
        meterProps={meterProps}
        close={popover.close}
        onHeightChange={handleHeightChange}
      />
    </Popover>
  );
}

function MobileMarketDistributionMeter({ order, meterProps }: { order: Order; meterProps: MeterWithValueProps }) {
  const mixpanel = useMixpanel();
  const { isModalOpen, openModal, pushModal } = useMobileModal();

  const openMarketDistribution = useCallback(() => {
    mixpanel.track(MixpanelEvent.ViewMarketDistributionTooltip, {
      [MixpanelEventProperty.Source]: MixpanelEventSource.OrdersBlotter,
    });

    const content = <MobileMarketDistributionSheet order={order} meterProps={meterProps} />;

    if (isModalOpen) {
      pushModal({ title: 'Market Distribution', useSheetContent: true, content });
    } else {
      openModal({ title: 'Market Distribution', useSheetContent: true, content });
    }
  }, [isModalOpen, meterProps, mixpanel, openModal, order, pushModal]);

  const stopCardPress = useCallback((e: MouseEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <Box w="100%" onClick={stopCardPress}>
      <Box w="100%" onClick={openMarketDistribution} data-testid="market-distribution-meter-target">
        <Meter {...meterProps} />
      </Box>
    </Box>
  );
}

export function MarketDistributionTooltipRenderer(props: { meterProps: MeterWithValueProps } & AgGridMeterProps) {
  const isMobileLayout = useIsMobileLayout();
  const { data, meterProps } = props;

  if (!data || !(data instanceof Order) || !data.OrderID || isOrderRejected(data.OrdStatus)) {
    return null;
  }

  if (isMobileLayout) {
    return <MobileMarketDistributionMeter order={data} meterProps={meterProps} />;
  }

  return <DesktopMarketDistributionPopover order={data} meterProps={meterProps} />;
}

interface FilledMarketDistributionProps {
  order: Order;
}

function FilledMarketDistribution({ order }: FilledMarketDistributionProps) {
  const security = useSecurity(order.Symbol);
  const isOrderMultiLeg = isMultileg(security);
  const isUnifiedLiquidity = order.unifiedLiquidity === UnifiedLiquidityEnum.Enabled;
  const [activeLeg, setActiveLeg] = useState<0 | 1 | undefined>(isOrderMultiLeg && !isUnifiedLiquidity ? 0 : undefined);

  useEffect(() => {
    if (!isOrderMultiLeg) {
      setActiveLeg(undefined);
      return;
    }

    if (!isUnifiedLiquidity && activeLeg == null) {
      setActiveLeg(0);
    }
  }, [activeLeg, isOrderMultiLeg, isUnifiedLiquidity]);

  const actions = useMemo(() => {
    const legOptions = [
      ...(isUnifiedLiquidity ? [{ value: MarketDistributionLegEnum.Order, displayName: 'Order' }] : []),
      { value: MarketDistributionLegEnum.Leg1, displayName: 'Leg 1' },
      { value: MarketDistributionLegEnum.Leg2, displayName: 'Leg 2' },
    ];

    const legValue =
      activeLeg == null
        ? MarketDistributionLegEnum.Order
        : activeLeg === 0
          ? MarketDistributionLegEnum.Leg1
          : MarketDistributionLegEnum.Leg2;

    return (
      isOrderMultiLeg && (
        <ToggleGroup<MarketDistributionLegEnum>
          options={legOptions}
          value={legValue}
          onChange={next => {
            if (next === MarketDistributionLegEnum.Order) {
              setActiveLeg(undefined);
              return;
            }
            setActiveLeg(next === MarketDistributionLegEnum.Leg1 ? 0 : 1);
          }}
          size={FormControlSizes.Small}
          data-testid="market-distribution-tooltip-leg-toggle"
        />
      )
    );
  }, [activeLeg, isOrderMultiLeg, isUnifiedLiquidity]);

  return (
    <Card actions={actions}>
      <FilledMarketDistributionChart order={order} legIndex={activeLeg} />
    </Card>
  );
}
